import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpService } from './http.service';
import { transformCamelToSnake, transformSnakeToCamel } from '../../store/utils/transformers';
import {
  DEFAULT_PENDING_LINKS_CURRENT_PAGE,
  DEFAULT_PENDING_LINKS_PER_PAGE,
  PendingLink,
  PendingLinkDecisionPayload,
  PendingLinksBulkDecidePayload,
  PendingLinksExportPayload,
  PendingLinksListParams,
  PendingLinksListResponse,
} from '../../store/entities/models/pending-link.model';

@Injectable({
  providedIn: 'root',
})
export class PendingLinksService {
  private http = inject(HttpService);

  getPendingLinks(params: PendingLinksListParams = {}): Observable<PendingLinksListResponse> {
    const queryParams: Record<string, unknown> = {
      page: params.page ?? DEFAULT_PENDING_LINKS_CURRENT_PAGE,
      per_page: params.perPage ?? DEFAULT_PENDING_LINKS_PER_PAGE,
      sort: params.sort ?? 'created_at',
      sort_direction: params.sortDirection ?? 'desc',
    };

    if (params.search) {
      queryParams.search = params.search;
    }

    if (params.status) {
      queryParams.status = params.status;
    }

    if (params.sourceSurface) {
      queryParams.source_surface = params.sourceSurface;
    }

    return this.http.get<Record<string, unknown>>('/pending-links', queryParams).pipe(
      map((response) => {
        const mapped = transformSnakeToCamel(response) as PendingLinksListResponse;
        const data = (mapped.data ?? []).map((item) => this.normalizePendingLink(item));

        return {
          data,
          meta: mapped.meta,
        };
      })
    );
  }

  approvePendingLink(payload: PendingLinkDecisionPayload): Observable<unknown> {
    return this.http.post(
      `/pending-links/${payload.linkId}/approve`,
      this.buildDecisionBody(payload)
    );
  }

  approveOncePendingLink(payload: PendingLinkDecisionPayload): Observable<unknown> {
    return this.http.post(
      `/pending-links/${payload.linkId}/approve-once`,
      this.buildDecisionBody(payload)
    );
  }

  rejectPendingLink(payload: PendingLinkDecisionPayload): Observable<unknown> {
    return this.http.post(
      `/pending-links/${payload.linkId}/reject`,
      this.buildDecisionBody(payload)
    );
  }

  bulkDecidePendingLinks(payload: PendingLinksBulkDecidePayload): Observable<unknown> {
    return this.http.post('/pending-links/bulk-decide', transformCamelToSnake(payload));
  }

  exportPendingLinks(payload: PendingLinksExportPayload): Observable<{ message?: string }> {
    const body: Record<string, unknown> = {
      sort: payload.sort ?? 'created_at',
      sort_direction: payload.sortDirection ?? 'desc',
    };

    if (payload.search) {
      body.search = payload.search;
    }

    if (payload.status) {
      body.status = payload.status;
    }

    if (payload.sourceSurface) {
      body.source_surface = payload.sourceSurface;
    }

    if (payload.allSelected) {
      body.all_selected = true;
    } else if (payload.linkIds?.length) {
      body.link_ids = payload.linkIds;
      body.all_selected = false;
    } else {
      body.all_selected = true;
    }

    return this.http.post<{ message?: string }>('/pending-links/export', body);
  }

  private buildDecisionBody(payload: PendingLinkDecisionPayload): Record<string, unknown> {
    const body: Record<string, unknown> = {
      domain_uuids: [payload.domainUuid].filter(Boolean),
    };

    if (payload.notes) {
      body.notes = payload.notes;
    }

    if (payload.reasonCode) {
      body.reason_code = payload.reasonCode;
    }

    if (payload.expiresAt) {
      body.expires_at = payload.expiresAt;
    }

    if (payload.ownerUuid) {
      body.owner_uuid = payload.ownerUuid;
    }

    if (payload.patternExpression) {
      body.pattern_expression = payload.patternExpression;
    }

    return body;
  }

  private normalizePendingLink(item: PendingLink): PendingLink {
    return {
      ...item,
      longUrl: item.longUrl ?? item.requestPayload?.longUrl,
    };
  }
}
